/**
 * "Open by default" — ONE home-screen install (the launcher at `/`) and a
 * persisted choice of which app it opens into. Replaces per-app manifests:
 * iOS Chrome's share sheet read the static document's manifest, so a
 * per-route manifest needed the HTML rewritten in the server AND the SW —
 * too many moving parts for one icon (Oct 2026).
 *
 * The choice applies ONLY when the shell actually boots at `/` from the
 * Home Screen — killing the PWA and relaunching it. Switching to another app
 * and back never reloads the page, so that resumes where it left off, as
 * before. A reload of the launcher, or arriving at `/` via "All apps", keeps
 * the launcher: those carry a navigation type of "reload" or a same-origin
 * referrer, while a Home Screen launch is a plain "navigate" with no referrer.
 *
 * Stored in localStorage (same as fin.tagPolicy) under `launch.default`.
 */
export type LaunchTarget = "launcher" | "vacations" | "finance";

export const LAUNCH_KEY = "launch.default";

export const LAUNCH_TARGETS: { id: LaunchTarget; label: string; href: string }[] = [
  { id: "launcher", label: "Launcher", href: "/" },
  { id: "vacations", label: "Vacations", href: "/trip" },
  { id: "finance", label: "Finances", href: "/finance" },
];

export function readLaunchPreference(): LaunchTarget {
  try {
    const raw = window.localStorage.getItem(LAUNCH_KEY);
    const v = raw ? (JSON.parse(raw) as string) : "launcher";
    return LAUNCH_TARGETS.some((t) => t.id === v) ? (v as LaunchTarget) : "launcher";
  } catch {
    return "launcher";
  }
}

/** A Home Screen launch (or a first open from the address bar): a plain
 * navigation with no referrer. Reloads and in-app links to `/` are not. */
export function isColdStart(): boolean {
  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const type = nav?.type ?? "navigate";
  return type === "navigate" && document.referrer === "";
}

/** Where a boot at `pathname` should go instead, or null to stay put. */
export function coldStartRedirect(pathname: string, pref: LaunchTarget = readLaunchPreference(), cold: boolean = isColdStart()): string | null {
  if (pathname !== "/" || !cold || pref === "launcher") return null;
  return LAUNCH_TARGETS.find((t) => t.id === pref)?.href ?? null;
}
